import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import classnames from 'classnames';
import LocalizedText from '@LocalizedText';
import DPO from 'dpo8core';
import useIntlMessage from '@app/hooks/use-intl-message';

import './SignupForm.scss';

const SignupForm = ({ onSignUp, signUpError }) => {
  const minPasswordLength = 8;
  const [invalidEmailError, requiredFirstNameError, requiredLastNameError, invalidNameError, passwordLengthError] = useIntlMessage(
    'qau-invalid-email-error',
    'qau-first-name-required',
    'qau-last-name-required',
    'qau-name-invalid',
    {
      id: 'qau-password-length-error',
      values: { minPasswordLength },
    },
  );
  const validatePassword = password => (password.length < minPasswordLength ? passwordLengthError : undefined);
  const validateEmail = email => {
    return DPO.project.Validator.signUpEmail(email) && invalidEmailError;
  };
  const validateName = (name, requiredError) => {
    if (!name.trim().length) {
      return requiredError;
    }
    return DPO.project.Validator.signUpName(name) ? invalidNameError : undefined;
  };
  const validateFirstName = name => validateName(name, requiredFirstNameError);
  const validateLastName = name => validateName(name, requiredLastNameError);

  return (
    <div id="signup-form">
      <div className="signup-form-title">
        <LocalizedText id="qau-create-account" />
      </div>
      <Formik
        initialValues={{
          email: '',
          firstName: '',
          lastName: '',
          password: '',
          optIn: false,
        }}
        onSubmit={onSignUp}
      >
        {({ errors, touched, values, dirty }) => (
          <Form>
            <label htmlFor="signup-email">
              <LocalizedText id="qau-email" tagName="span" />
              <Field
                type="email"
                name="email"
                id="signup-email"
                validate={validateEmail}
                maxLength="100"
                className={classnames(
                  { invalid: (errors.email && touched.email) || signUpError },
                  { valid: !errors.email && touched.email && values.email.length > 1 },
                )}
              />
              <span className="email-icons" />
              {errors.email && touched.email && <div className="error-msg">{errors.email}</div>}
              {signUpError && <div className="error-msg">{signUpError}</div>}
            </label>
            <div id="name-fields">
              <label htmlFor="signup-first-name">
                <LocalizedText id="qau-sign-up-first-name" />
                <Field
                  type="text"
                  name="firstName"
                  id="signup-first-name"
                  validate={validateFirstName}
                  maxLength="100"
                  className={classnames(
                    { invalid: errors.firstName && touched.firstName },
                    { valid: !errors.firstName && touched.firstName && values.firstName.length > 1 },
                  )}
                />
                <span className="name-icons" />
                {errors.firstName && touched.firstName && <div className="error-msg">{errors.firstName}</div>}
              </label>
              <label htmlFor="signup-last-name">
                <LocalizedText id="qau-sign-up-last-name" />
                <Field
                  type="text"
                  name="lastName"
                  id="signup-last-name"
                  validate={validateLastName}
                  maxLength="100"
                  className={classnames(
                    { invalid: errors.lastName && touched.lastName },
                    { valid: !errors.lastName && touched.lastName && values.lastName.length > 1 },
                  )}
                />
                <span className="name-icons" />
                {errors.lastName && touched.lastName && <div className="error-msg">{errors.lastName}</div>}
              </label>
            </div>
            <label htmlFor="signup-password" className="required">
              <LocalizedText id="qau-password" tagName="span" />
              <Field
                type="password"
                name="password"
                id="signup-password"
                validate={validatePassword}
                maxLength="100"
                className={classnames({ invalid: errors.password && touched.password }, { valid: !errors.password && dirty })}
              />
              <span className="password-icons" />
              <div className="password-tip">
                <span className="password-length">
                  {values.password.length}/{minPasswordLength}
                </span>
                <LocalizedText id="qau-password-tip" />
              </div>
            </label>
            <label htmlFor="subscribe-newsletter">
              <Field type="checkbox" name="optIn" id="subscribe-newsletter" checked={values.optIn} />
              <LocalizedText id="qau-signup-newsletter-text" tagName="span" />
            </label>
            <div className="signup-acknowledge">
              <LocalizedText id="qau-signup-acknowledge-tos" />
            </div>
            <button className="submit-btn" type="submit">
              <LocalizedText id="qau-create-account" />
            </button>
          </Form>
        )}
      </Formik>

      <div className="terms-and-privacy">
        <LocalizedText id="qau-terms-and-privacy" />
      </div>
    </div>
  );
};

SignupForm.propTypes = {
  onSignUp: PropTypes.func.isRequired,
  signUpError: PropTypes.string,
};

export default SignupForm;
